import { createContext, useContext, useEffect, useMemo, useState } from "react";

/**
 * Lets a screen publish its title and back-target up to the mobile top bar, so
 * the phone layout gets a real native-style header without every screen having
 * to render one itself.
 *
 * Title and back-target travel through state (they are strings, so they compare
 * cheaply). Header *actions* are JSX and would change identity on every render,
 * so they are portalled into a slot the top bar owns instead of stored in state
 * — putting them in a dependency array would re-fire the effect forever.
 */
const ShellContext = createContext({
  pageMeta: {},
  setPageMeta: () => {},
  actionsSlot: null,
  setActionsSlot: () => {},
  isMobile: false,
});

export const useShell = () => useContext(ShellContext);

export function useShellState() {
  const [pageMeta, setPageMeta] = useState({});
  const [actionsSlot, setActionsSlot] = useState(null);
  return useMemo(
    () => ({ pageMeta, setPageMeta, actionsSlot, setActionsSlot }),
    [pageMeta, actionsSlot]
  );
}

/** Registers title/back-target for as long as the calling screen is mounted. */
export function usePageMeta({ title, backTo } = {}) {
  const { setPageMeta } = useShell();

  useEffect(() => {
    setPageMeta({ title, backTo });
    return () => setPageMeta({});
  }, [title, backTo, setPageMeta]);
}

export default ShellContext;
